/*
 * © 2025 Fannie Mae. All rights reserved.
 * This software contains confidential information and trade secrets of Fannie Mae.
 * Use, disclosure, or reproduction is prohibited without the prior written consent of Fannie Mae.
 */
import { Button } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import "./AssignReviewerModal.css";
import { Autocomplete, FormControlLabel, FormGroup, Switch, TextField, TextareaAutosize, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { assignReviewerToRequest, fetchReviewers } from "../../shared/services/LenderRequestService";
import {isProjectRole, isInternal}  from "../../../redux/actions/userAction";

export const AssignReviewerModal = (props) => {
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [error, setError] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [escalate, setEscalate] = useState(false);
  const [notesExternal, setNotesExternal] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesBoxActive, setNotesBoxActive] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadReviewers = async () => {
      // If reviewers are provided via props, use them
      if (props.reviewers && props.reviewers.length > 0) {
        setReviewers(props.reviewers);
        setSelectedReviewerFromProps(props.reviewers);
      } else if (isProjectRole() && isInternal()) {
        // Otherwise, fetch reviewers from the service
        try {
          let reviewersList = await fetchReviewers();
          // Sort the reviewers alphabetically by userName
          reviewersList = reviewersList.sort((a, b) => {
            const nameA = a.userName?.toLowerCase() || ""; 
            const nameB = b.userName?.toLowerCase() || "";
            return nameA.localeCompare(nameB);
          });
          setReviewers(reviewersList);
          setSelectedReviewerFromProps(reviewersList);
        } catch (error) {
          console.error("Error fetching reviewers:", error);
        }
      }
    };

    loadReviewers();
  }, [props.reviewers, props.selectedRequest?.assignedToLdapId]);

  const setSelectedReviewerFromProps = (reviewersList) => {
    // Set selected reviewer if assignedToLdapId is present
    if (props.selectedRequest?.assignedToLdapId) {
      const currentReviewer = reviewersList.find(reviewer => 
        reviewer.userLdapId === props.selectedRequest.assignedToLdapId
      );
      if (currentReviewer) {
        setSelectedReviewer(currentReviewer);
      }
    }
  };

  function onSelectReviewer(reviewer) {
    setSelectedReviewer(reviewer);
    setError(!reviewer && !escalate);
  }

  function onToggleEscalate(ev) {
    setEscalate(ev.target.checked);
    if (ev.target.checked) setError(false);
    if (!ev.target.checked) setNotesError("");
  }

  const onShow = () => {
    setSelectedReviewer(null);
    setError(false);
    setNotes("");
    setEscalate(false);
    setNotesExternal(false);
    setNotesError("");
    setSubmitting(false);
  };

  const onHide = () => {
    setSelectedReviewer(null);
    setError(false);
    setNotes("");
    setEscalate(false);
    setNotesExternal(false);
    setNotesError("");
    setSubmitting(false);
    props.setShow(false);
  };

  const onSubmit = async () => {
    if (submitting) return;
    // Reviewer is required only when NOT escalating
    if (!selectedReviewer && !escalate) {
      setError(true);
      return;
    }
    setError(false);
    if (escalate && !notes.trim()) {
      setNotesError("This field is required");
      return;
    }
    
    setSubmitting(true);
    try {
      // Check if parent provides custom handler (e.g., LenderDashboard)
      if (props.customHandler) {
        const status = await props.customHandler(selectedReviewer, notes, escalate, notesExternal);
        if (status) {
          onHide();
        } else {
          console.error("Custom assignment handler failed");
          onHide(); // Still close to prevent stuck modal
        }
        return;
      }

      // Default assignment flow for other components
      const passData = JSON.parse(sessionStorage.getItem("passData"));
      const request = {
        requestStatusId: props.selectedRequest.projectRequestStatusId,  
        requestMasterTrackerId: props.selectedRequest.requestId,
        assignTo: selectedReviewer ? selectedReviewer.userLdapId : null,
        notes: notes,
        isEscalated: escalate,
        notesIsExternal: notesExternal,
        createdBy: passData.user,
        updatedBy: passData.user,
      };
      
      const status = await assignReviewerToRequest(request);
      
      if (status) {
        
        // Dispatch assignment completion event for background refresh
        document.dispatchEvent(new CustomEvent("assignment-complete", { 
          detail: { 
            requestId: props.selectedRequest.requestId,
            assignedTo: selectedReviewer ? selectedReviewer.userLdapId : null,
            assignedToName: selectedReviewer ? selectedReviewer.userName : null,
            source: props.source || 'default'  // Add source identification
          } 
        }));
        
        // Call onAssignComplete callback if provided (for Project/Request pages)
        if (props.onAssignComplete) {
          props.onAssignComplete();
        }
        
        // Close the modal after successful assignment
        onHide();
      } else {
        console.error("Failed to assign reviewer - API returned false");
        // Still close modal even on API failure to prevent stuck modal
        onHide();
        // Dispatch assignment error event
        document.dispatchEvent(new CustomEvent("assignment-error", { 
          detail: { 
            requestId: props.selectedRequest.requestId,
            error: "Failed to assign reviewer",
            source: props.source || 'default'
          } 
        }));
      }
    } catch (error) {
      console.error("Error during assignment:", error);
      // Still close modal even on error to prevent stuck modal
      onHide();
      // Dispatch assignment error event
      document.dispatchEvent(new CustomEvent("assignment-error", { 
        detail: { 
          requestId: props.selectedRequest.requestId,
          error: error.message || "Unknown error during assignment",
          source: props.source || 'default'
        } 
      }));
    }
  };

  return (
    <Modal className="reviewer-modal" aria-label="Assign to Reviewer" show={props.show} onShow={onShow} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Assign to Reviewer</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        <form className="w-80 pl-3">
          <div className="form-group">
            <Autocomplete
              id="reviewer"
              options={reviewers || []}
              value={selectedReviewer}
              getOptionLabel={(option) => option.userName || ""}
              getOptionKey={(option) => option.userLdapId || ""}
              sx={{ width: 300, display: "inline-block", marginTop: "5px" }}
              renderInput={(params) => 
                <TextField 
                  {...params} 
                  placeholder="Select Reviewer" 
                  error={error}
                  helperText={error ? "Please select a reviewer." : ""} 
                />
              }
              onChange={(ev, value) => onSelectReviewer(value)}
            />
            {!escalate && <span style={{color: "#f00", marginLeft: "5px"}}> *</span>}
          </div>
          <FormGroup>
            <FormControlLabel control={<Switch checked={escalate} onChange={onToggleEscalate} />} label="Escalate" />
          </FormGroup>
          <div className="form-group mt-3" style={{ position: "relative", paddingBottom: notesExternal === "both" ? "50px" : "0px" }}>
            <label htmlFor="notes">Notes{escalate && <span style={{ color: "#f00" }}> *</span>}</label>
            <div
              className={`notes-box${notesBoxActive ? " notes-box-active" : ""}${notesError ? " has-error" : ""}`}
              style={{ position: "relative", width: "95%", minWidth: 0, background: '#fff', borderRadius: '4px', display: 'flex', flexDirection: 'column' }}
            >
              <TextareaAutosize
                id="notes"
                minRows={3}
                placeholder="Add notes here..."
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setNotesBoxActive(true);
                  if (notesError) setNotesError("");
                }}
                onFocus={() => setNotesBoxActive(true)}
                onBlur={() => setNotesBoxActive(false)}
                style={{ width: "100%", marginTop: "5px", border: 'none', outline: 'none', resize: 'vertical', background: 'transparent', maxHeight: '120px', overflow: 'auto', flex: '1 1 auto' }}
              />
              <div className="toggle-container" style={{ marginTop: '8px', alignSelf: 'flex-end' }}>
                <ToggleButtonGroup
                  color="primary"
                  value={notesExternal ? "both" : "internal"}
                  exclusive
                  onChange={(event, value) => {
                    setNotesExternal(value === "both");
                  }}
                  aria-label="text alignment"
                  size="small"
                >
                  <ToggleButton value="internal" aria-label="left aligned">
                    Internal
                  </ToggleButton>
                  <ToggleButton value="both" aria-label="centered">
                    Internal & External
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>
            </div>
            {notesError && (
              <span role="alert" style={{ color: "#990000", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
                {notesError}
              </span>
            )}
            {notesExternal && (
              <div className="comment-note" style={{ marginTop: "8px", color: '#1176B5', fontWeight: 'bold', background: '#fff', padding: '4px 8px', borderRadius: '4px', width: '100%' }}>
                This will allow lenders to view this note; use standardized language when possible.
              </div>
            )}
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: "#f7f7f7" }}>
        <Button className="submit-button" type="button" onClick={onSubmit} disabled={submitting}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};